import React, { memo, useCallback, useMemo, useState } from 'react'
import AddIcon from 'transfix-icons/build/AddIcon'
import RefreshIcon from 'transfix-icons/build/RefreshIcon'
import Button from 'transfix-ui/components/Button'
import CurrencyInput from 'transfix-ui/components/CurrencyInput'
import InlineMessage, { INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme'
import { IBid, Maybe } from 'types/graphqlTypes'
import { useModalState } from 'utils/hooks/useModalState'
import { formatRate } from 'utils/formatRate'
import {
  CounterOfferCardOverflowMenu,
  CreateCounterOfferConfirmationModal,
  DeleteCounterOfferConfirmationModal,
  UpdateCounterOfferConfirmationModal,
} from './components'
import { TEST_IDS } from './constants'

const useCounterOfferCardStyles = makeStyles(theme => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    backgroundColor: brandColors.skyBlue1,
    padding: theme.spacing(1.5),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  buttons: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    gap: theme.spacing(1),
  },
}))

export interface ICounterOfferCardProps {
  parentBidId: IBid['id']
  parentBidAmount: Maybe<IBid['amount']>
  parentBidStatus: IBid['status']
  counterBid?: Maybe<
    Pick<
      IBid,
      'id' | 'amount' | 'carrierContactPhone' | 'carrierContactPhoneExt' | 'shipmentId' | 'status'
    >
  >
}

export const CounterOfferCard = memo<ICounterOfferCardProps>(
  ({ counterBid, parentBidAmount, parentBidId, parentBidStatus }) => {
    const classes = useCounterOfferCardStyles()

    const {
      amount: counterAmount,
      carrierContactPhone,
      carrierContactPhoneExt,
      id: counterOfferId,
      shipmentId,
      status,
    } = counterBid ?? {}

    const counterOfferAmount = useMemo(() => {
      const isCounterBidActive = status === null
      const isCounterBidAccepted = status === 'Accepted'
      if (isCounterBidActive || isCounterBidAccepted) {
        return counterAmount
      }
      return undefined
    }, [counterAmount, status])

    const [amount, setAmount] = useState<number | undefined>(counterOfferAmount)
    const [isEditing, setIsEditing] = useState<boolean>(false)
    const [isCreating, setIsCreating] = useState<boolean>(false)

    const isParentBidDeclined = useMemo(() => {
      return parentBidStatus === 'Declined'
    }, [parentBidStatus])

    const showAddButton = useMemo(() => {
      if (isParentBidDeclined) {
        return false
      }

      if (isEditing || isCreating) {
        return false
      }
      return !counterOfferAmount
    }, [counterOfferAmount, isCreating, isEditing, isParentBidDeclined])

    const isAmountInvalid = useMemo(() => {
      if (!amount) {
        return false
      }
      if (!parentBidAmount) {
        return amount <= 0
      }
      return amount <= 0 || amount >= parentBidAmount
    }, [amount, parentBidAmount])

    const counterOfferAmountChangeHandler = useCallback(({ value }: { value: string }) => {
      setAmount(Number(value))
    }, [])

    const {
      createToggleHandler,
      modalState: {
        createCounterOfferModal: isCreateCounterOfferModalOpen,
        deleteCounterOfferModal: isDeleteCounterOfferModalOpen,
        updateCounterOfferModal: isUpdateCounterOfferModalOpen,
      },
    } = useModalState({
      createCounterOfferModal: false,
      deleteCounterOfferModal: false,
      updateCounterOfferModal: false,
    })
    const openCreateCounterOfferModal = createToggleHandler('createCounterOfferModal', true)
    const closeCreateCounterOfferModal = createToggleHandler('createCounterOfferModal', false)
    const openDeleteCounterOfferModal = createToggleHandler('deleteCounterOfferModal', true)
    const closeDeleteCounterOfferModal = createToggleHandler('deleteCounterOfferModal', false)
    const openUpdateCounterOfferModal = createToggleHandler('updateCounterOfferModal', true)
    const closeUpdateCounterOfferModal = createToggleHandler('updateCounterOfferModal', false)

    const cancelClickHandler = useCallback(() => {
      setAmount(counterOfferAmount)
      setIsEditing(false)
      setIsCreating(false)
    }, [counterOfferAmount])

    const deleteClickHandler = useCallback(() => {
      openDeleteCounterOfferModal()
    }, [openDeleteCounterOfferModal])

    const createClickHandler = useCallback(() => {
      setIsCreating(true)
    }, [])

    const editClickHandler = useCallback(() => {
      setIsEditing(true)
    }, [])

    const successHandler = useCallback(() => {
      if (isCreateCounterOfferModalOpen) {
        closeCreateCounterOfferModal()
      } else if (isDeleteCounterOfferModalOpen) {
        closeDeleteCounterOfferModal()
      } else if (isUpdateCounterOfferModalOpen) {
        closeUpdateCounterOfferModal()
      }
      setIsCreating(false)
      setIsEditing(false)
    }, [
      closeCreateCounterOfferModal,
      closeDeleteCounterOfferModal,
      closeUpdateCounterOfferModal,
      isCreateCounterOfferModalOpen,
      isDeleteCounterOfferModalOpen,
      isUpdateCounterOfferModalOpen,
    ])

    const formattedTitle = useMemo(() => {
      if (!counterOfferAmount) {
        return 'Counter Offer'
      }

      return `Counter Offer: ${formatRate(counterOfferAmount)}`
    }, [counterOfferAmount])

    return (
      <>
        {!showAddButton && !isParentBidDeclined && (
          <div className={classes.card} data-test={TEST_IDS.counterOfferContainer}>
            <div className={classes.header}>
              <div className={classes.title}>
                <RefreshIcon size='large' color='skyBlue6' />
                <Typography variant='subtitle2'>{formattedTitle}</Typography>
              </div>
              <div>
                <CounterOfferCardOverflowMenu
                  disabled={isEditing || isCreating}
                  onEditClick={editClickHandler}
                  onDeleteClick={deleteClickHandler}
                />
              </div>
            </div>
            {(isEditing || isCreating) && (
              <>
                {isAmountInvalid && (
                  <InlineMessage
                    message={`Counter offer amount must be less than ${formatRate(
                      parentBidAmount
                    )}`}
                    type={INLINE_MESSAGE_VARIANTS.ALERT}
                    fullWidth
                  />
                )}
                <CurrencyInput
                  value={amount}
                  onChange={counterOfferAmountChangeHandler}
                  dataTest={TEST_IDS.counterOfferAmountInput}
                  large
                />
                <div className={classes.buttons}>
                  <Button variant='tertiary' fullWidth onClick={cancelClickHandler}>
                    Cancel
                  </Button>
                  <Button
                    fullWidth
                    disabled={isAmountInvalid}
                    onClick={
                      isCreating ? openCreateCounterOfferModal : openUpdateCounterOfferModal
                    }>
                    {isCreating ? 'Create' : 'Update'}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {showAddButton && (
          <Button
            data-test={TEST_IDS.addCounterOfferButton}
            color='primary'
            startIcon={<AddIcon color='skyBlue6' size='large' />}
            variant='text'
            onClick={createClickHandler}
            noPadding>
            Add Counter Offer
          </Button>
        )}

        {isCreateCounterOfferModalOpen && parentBidId && amount && (
          <CreateCounterOfferConfirmationModal
            amount={amount}
            parentBidId={parentBidId}
            onSuccess={successHandler}
            onClose={closeCreateCounterOfferModal}
          />
        )}

        {isUpdateCounterOfferModalOpen &&
          counterOfferId &&
          amount &&
          shipmentId &&
          carrierContactPhone && (
            <UpdateCounterOfferConfirmationModal
              amount={amount}
              counterOfferId={counterOfferId}
              shipmentId={shipmentId}
              carrierContactPhone={carrierContactPhone}
              carrierContactPhoneExt={carrierContactPhoneExt}
              onSuccess={successHandler}
              onClose={closeUpdateCounterOfferModal}
            />
          )}

        {isDeleteCounterOfferModalOpen && counterOfferId && (
          <DeleteCounterOfferConfirmationModal
            counterOfferId={counterOfferId}
            onSuccess={successHandler}
            onClose={closeDeleteCounterOfferModal}
          />
        )}
      </>
    )
  }
)

export default CounterOfferCard
